import React, { useState, useCallback } from 'react';
import './ServiceSelect.css';

const SERVICES = [
  { id: 'pastPrediction',     icon: '📖', label: 'Past Prediction',      desc: 'Chart + Life events' },
  { id: 'futureConsultation', icon: '🔮', label: 'Future Consultation',   desc: 'Upcoming periods' },
  { id: 'remedies',           icon: '💊', label: 'Remedies',              desc: 'Planetary remedies' },
  { id: 'palmistry',          icon: '✋', label: 'Palmistry Reading',     desc: 'Hand analysis' },
  { id: 'astroVastu',         icon: '🏠', label: 'Astro Vastu',           desc: 'Home energy' },
  { id: 'numerology',         icon: '🔢', label: 'Numerology',            desc: 'Name + birth numbers' },
  { id: 'tarot',              icon: '🃏', label: 'Tarot Reading',         desc: 'Card guidance' },
  { id: 'compatibility',      icon: '💑', label: 'Compatibility',         desc: 'Kundli matching' },
  { id: 'horoscopePDF',       icon: '📄', label: 'Horoscope PDF',         desc: 'Full report' },
  { id: 'tenYearPDF',         icon: '📅', label: '10 Year Life PDF',      desc: '10-year forecast' },
];

const LANGUAGES  = ['Telugu', 'Hindi', 'English', 'Telugu-Roman', 'Hindi-Roman'];
const GENDERS    = ['Male', 'Female', 'Other'];
const TAROT_AREAS  = ['General', 'Love', 'Career', 'Finance', 'Health', 'Spiritual'];
const DIRECTIONS   = ['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West'];
const PURPOSES     = ['Marriage', 'Business', 'Friendship', 'Love Relationship'];

// ── Which services need birth details ──
const NEEDS_BIRTH = new Set([
  'pastPrediction', 'futureConsultation', 'remedies',
  'horoscopePDF', 'tenYearPDF', 'compatibility',
]);
const NEEDS_DOB_ONLY = new Set(['numerology']);

// ── BirthForm — module level so React never unmounts/remounts it on re-render ──
const BirthForm = ({ prefix = '', data, onChange }) => (
  <div className="ss-form">
    <div className="ss-form__field ss-form__field--full">
      <label className="ss-form__label">Client Name *</label>
      <input className="ss-form__input" type="text" placeholder="Full name"
        value={data.name} onChange={e => onChange('name', e.target.value)} />
    </div>
    <div className="ss-form__field">
      <label className="ss-form__label">Date of Birth *</label>
      <input className="ss-form__input" type="date"
        value={data.dob} onChange={e => onChange('dob', e.target.value)} />
    </div>
    <div className="ss-form__field">
      <label className="ss-form__label">Time of Birth</label>
      <input className="ss-form__input" type="time"
        value={data.tob} onChange={e => onChange('tob', e.target.value)} />
    </div>
    <div className="ss-form__field ss-form__field--full">
      <label className="ss-form__label">Place of Birth *</label>
      <input className="ss-form__input" type="text" placeholder="City, State, Country"
        value={data.pob} onChange={e => onChange('pob', e.target.value)} />
    </div>
    <div className="ss-form__field">
      <label className="ss-form__label">Gender</label>
      <div className="ss-toggle">
        {GENDERS.map(g => (
          <button key={g}
            className={`ss-toggle__btn${data.gender === g ? ' ss-toggle__btn--on' : ''}`}
            onClick={() => onChange('gender', g)}>{g}</button>
        ))}
      </div>
    </div>
    {!prefix && (
      <div className="ss-form__field">
        <label className="ss-form__label">Language</label>
        <select className="ss-form__input ss-form__select"
          value={data.language} onChange={e => onChange('language', e.target.value)}>
          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
    )}
  </div>
);

// ── ServiceSelect ──────────────────────────────────────────────────────────────
const ServiceSelect = ({ onStart, onLogout }) => {
  const [step,     setStep]     = useState('service'); // 'service' | 'form'
  const [selected, setSelected] = useState(null);

  // Base client fields
  const [form, setForm] = useState({
    name: '', dob: '', tob: '', pob: '',
    gender: 'Male', language: 'Telugu',
  });

  // Service-specific extra fields
  const [extra, setExtra] = useState({
    concern:       '',          // palmistry
    doorDirection: 'North',     // vastu
    roomConcerned: '',          // vastu
    problems:      '',          // vastu
    question:      '',          // tarot
    area:          'General',   // tarot
    purpose:       'Marriage',  // compatibility
    person2: { name: '', dob: '', tob: '', pob: '', gender: 'Female' },
  });

  const set   = useCallback((k, v) => setForm(f  => ({ ...f,  [k]: v })),                                   []);
  const setEx = useCallback((k, v) => setExtra(e => ({ ...e,  [k]: v })),                                   []);
  const setP2 = useCallback((k, v) => setExtra(e => ({ ...e, person2: { ...e.person2, [k]: v } })),         []);

  const handleSelectService = svcId => {
    setSelected(svcId);
    setStep('form');
  };

  const handleBack = () => setStep('service');

  // ── Validation ──────────────────────────────────────────────────────────────
  const getValidation = () => {
    const { name, dob, pob } = form;
    if (!name.trim()) return { valid: false, hint: 'Enter client name' };

    if (NEEDS_BIRTH.has(selected)) {
      if (selected === 'compatibility') {
        const p2 = extra.person2;
        if (!dob)             return { valid: false, hint: 'Enter Person 1 date of birth' };
        if (!pob.trim())      return { valid: false, hint: 'Enter Person 1 place of birth' };
        if (!p2.name.trim())  return { valid: false, hint: 'Enter Person 2 name' };
        if (!p2.dob)          return { valid: false, hint: 'Enter Person 2 date of birth' };
        if (!p2.pob.trim())   return { valid: false, hint: 'Enter Person 2 place of birth' };
      } else {
        if (!dob)        return { valid: false, hint: 'Enter date of birth' };
        if (!pob.trim()) return { valid: false, hint: 'Enter place of birth' };
      }
    } else if (NEEDS_DOB_ONLY.has(selected)) {
      if (!dob) return { valid: false, hint: 'Enter date of birth' };
    } else if (selected === 'tarot') {
      if (!extra.question.trim()) return { valid: false, hint: 'Enter client question' };
    }
    return { valid: true, hint: '' };
  };

  const { valid, hint } = getValidation();

  const handleStart = () => {
    // Build clientData (base fields only)
    const clientData = {
      name:     form.name.trim(),
      gender:   form.gender,
      language: form.language,
      ...(NEEDS_BIRTH.has(selected) || NEEDS_DOB_ONLY.has(selected) ? {
        dob: form.dob,
        tob: form.tob,
        pob: form.pob.trim(),
      } : {}),
    };

    // Build extraData (service-specific)
    const extraData = {
      fullName:      form.name.trim(),
      clientQuestion: extra.question || extra.concern || '',
      concern:        extra.concern,
      tarotArea:      extra.area,
      doorDirection:  extra.doorDirection,
      roomConcerned:  extra.roomConcerned,
      problems:       extra.problems,
      purpose:        extra.purpose,
      person2: {
        name:   extra.person2.name.trim(),
        dob:    extra.person2.dob,
        tob:    extra.person2.tob,
        pob:    extra.person2.pob.trim(),
        gender: extra.person2.gender,
      },
    };

    onStart(selected, clientData, extraData);
  };

  // ── Render service-specific form ──────────────────────────────────────────────
  const renderForm = () => {
    // Birth chart services
    if (NEEDS_BIRTH.has(selected) && selected !== 'compatibility') {
      return (
        <>
          <BirthForm data={form} onChange={set} />
          <LangRow form={form} set={set} />
        </>
      );
    }

    if (selected === 'compatibility') {
      return (
        <>
          <div className="ss-compat-grid">
            <div className="ss-compat-person">
              <div className="ss-compat-person__label">Person 1</div>
              <BirthForm data={form} onChange={set} />
            </div>
            <div className="ss-compat-person">
              <div className="ss-compat-person__label">Person 2</div>
              <div className="ss-form">
                {[['name','Name *','text','Full name'],['dob','Date of Birth *','date',''],['tob','Time of Birth','time',''],['pob','Place of Birth *','text','City, State']].map(([k,lbl,type,ph]) => (
                  <div key={k} className={`ss-form__field${k==='name'||k==='pob'?' ss-form__field--full':''}`}>
                    <label className="ss-form__label">{lbl}</label>
                    <input className="ss-form__input" type={type} placeholder={ph}
                      value={extra.person2[k]} onChange={e => setP2(k, e.target.value)} />
                  </div>
                ))}
                <div className="ss-form__field">
                  <label className="ss-form__label">Gender</label>
                  <div className="ss-toggle">
                    {GENDERS.map(g => (
                      <button key={g}
                        className={`ss-toggle__btn${extra.person2.gender === g ? ' ss-toggle__btn--on' : ''}`}
                        onClick={() => setP2('gender', g)}>{g}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="ss-form ss-form--extra">
            <div className="ss-form__field">
              <label className="ss-form__label">Purpose</label>
              <select className="ss-form__input ss-form__select"
                value={extra.purpose} onChange={e => setEx('purpose', e.target.value)}>
                {PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="ss-form__field">
              <label className="ss-form__label">Language</label>
              <select className="ss-form__input ss-form__select"
                value={form.language} onChange={e => set('language', e.target.value)}>
                {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
        </>
      );
    }

    if (selected === 'numerology') {
      return (
        <div className="ss-form">
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Full Name * (as per birth certificate)</label>
            <input className="ss-form__input" type="text" placeholder="Complete legal name"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Date of Birth *</label>
            <input className="ss-form__input" type="date"
              value={form.dob} onChange={e => set('dob', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Gender</label>
            <div className="ss-toggle">
              {GENDERS.map(g => (
                <button key={g}
                  className={`ss-toggle__btn${form.gender === g ? ' ss-toggle__btn--on' : ''}`}
                  onClick={() => set('gender', g)}>{g}</button>
              ))}
            </div>
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Language</label>
            <select className="ss-form__input ss-form__select"
              value={form.language} onChange={e => set('language', e.target.value)}>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="ss-form__note ss-form__field--full">
            No place or time of birth needed for numerology.
          </div>
        </div>
      );
    }

    if (selected === 'palmistry') {
      return (
        <div className="ss-form">
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Client Name *</label>
            <input className="ss-form__input" type="text" placeholder="Full name"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Gender</label>
            <div className="ss-toggle">
              {GENDERS.map(g => (
                <button key={g}
                  className={`ss-toggle__btn${form.gender === g ? ' ss-toggle__btn--on' : ''}`}
                  onClick={() => set('gender', g)}>{g}</button>
              ))}
            </div>
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Language</label>
            <select className="ss-form__input ss-form__select"
              value={form.language} onChange={e => set('language', e.target.value)}>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Specific Concern (optional)</label>
            <input className="ss-form__input" type="text"
              placeholder="e.g. health, marriage, career, financial problems"
              value={extra.concern} onChange={e => setEx('concern', e.target.value)} />
          </div>
          <div className="ss-form__note ss-form__field--full">
            No birth details needed. Upload palm photos inside the session.
          </div>
        </div>
      );
    }

    if (selected === 'astroVastu') {
      return (
        <div className="ss-form">
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Client Name *</label>
            <input className="ss-form__input" type="text" placeholder="Full name"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Main Door Direction *</label>
            <select className="ss-form__input ss-form__select"
              value={extra.doorDirection} onChange={e => setEx('doorDirection', e.target.value)}>
              {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Language</label>
            <select className="ss-form__input ss-form__select"
              value={form.language} onChange={e => set('language', e.target.value)}>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Specific Room / Area Concerned (optional)</label>
            <input className="ss-form__input" type="text"
              placeholder="e.g. kitchen, master bedroom, puja room"
              value={extra.roomConcerned} onChange={e => setEx('roomConcerned', e.target.value)} />
          </div>
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Current Problems (optional)</label>
            <textarea className="ss-form__input ss-form__textarea" rows={2}
              placeholder="e.g. financial losses, health issues, relationship problems"
              value={extra.problems} onChange={e => setEx('problems', e.target.value)} />
          </div>
          <div className="ss-form__note ss-form__field--full">
            No birth details needed. Upload house photos inside the session.
          </div>
        </div>
      );
    }

    if (selected === 'tarot') {
      return (
        <div className="ss-form">
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Client Name *</label>
            <input className="ss-form__input" type="text" placeholder="Full name"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Gender</label>
            <div className="ss-toggle">
              {GENDERS.map(g => (
                <button key={g}
                  className={`ss-toggle__btn${form.gender === g ? ' ss-toggle__btn--on' : ''}`}
                  onClick={() => set('gender', g)}>{g}</button>
              ))}
            </div>
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Reading Area</label>
            <div className="ss-area-row">
              {TAROT_AREAS.map(a => (
                <button key={a}
                  className={`ss-area-btn${extra.area === a ? ' ss-area-btn--on' : ''}`}
                  onClick={() => setEx('area', a)}>{a}</button>
              ))}
            </div>
          </div>
          <div className="ss-form__field ss-form__field--full">
            <label className="ss-form__label">Client's Question * (what they want to know)</label>
            <textarea className="ss-form__input ss-form__textarea" rows={2}
              placeholder="e.g. Will I get married this year? Should I change my job?"
              value={extra.question} onChange={e => setEx('question', e.target.value)} />
          </div>
          <div className="ss-form__field">
            <label className="ss-form__label">Language</label>
            <select className="ss-form__input ss-form__select"
              value={form.language} onChange={e => set('language', e.target.value)}>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="ss-form__note ss-form__field--full">
            No birth details needed.
          </div>
        </div>
      );
    }

    return null;
  };

  const svcObj = SERVICES.find(s => s.id === selected);

  // ── STEP 1: Service grid ───────────────────────────────────────────────────
  if (step === 'service') {
    return (
      <div className="ss-page">
        <header className="ss-header">
          <div className="ss-header__brand">
            <SsLogo />
            <div>
              <div className="ss-header__title">VTD AI Astrologer</div>
              <div className="ss-header__sub">Select service to begin</div>
            </div>
          </div>
          <button className="ss-header__logout" onClick={onLogout}>Logout</button>
        </header>

        <div className="ss-body">
          <section className="ss-card">
            <h2 className="ss-card__title">Select Service</h2>
            <div className="ss-grid">
              {SERVICES.map(s => (
                <button key={s.id}
                  className="ss-svc"
                  onClick={() => handleSelectService(s.id)}>
                  <span className="ss-svc__icon">{s.icon}</span>
                  <span className="ss-svc__label">{s.label}</span>
                  <span className="ss-svc__desc">{s.desc}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ── STEP 2: Service-specific form ─────────────────────────────────────────
  return (
    <div className="ss-page">
      <header className="ss-header">
        <div className="ss-header__brand">
          <SsLogo />
          <div>
            <div className="ss-header__title">VTD AI Astrologer</div>
            <div className="ss-header__sub">{svcObj?.icon} {svcObj?.label}</div>
          </div>
        </div>
        <button className="ss-header__logout" onClick={onLogout}>Logout</button>
      </header>

      <div className="ss-body">
        {/* Back + service label */}
        <div className="ss-form-header">
          <button className="ss-back-btn" onClick={handleBack}>← Back to Services</button>
          <div className="ss-form-header__svc">
            <span className="ss-form-header__icon">{svcObj?.icon}</span>
            <span className="ss-form-header__name">{svcObj?.label}</span>
          </div>
        </div>

        <section className="ss-card">
          <h2 className="ss-card__title">Client Details</h2>
          {renderForm()}
        </section>

        <div className="ss-start">
          <button className="ss-start__btn" disabled={!valid} onClick={handleStart}>
            Start Session →
          </button>
          {hint && <p className="ss-start__hint">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

// ── Shared language row (used by birth-chart forms) ────────────────────────────
const LangRow = ({ form, set }) => (
  <div className="ss-form ss-form--extra">
    <div className="ss-form__field">
      <label className="ss-form__label">Language</label>
      <select className="ss-form__input ss-form__select"
        value={form.language} onChange={e => set('language', e.target.value)}>
        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
      </select>
    </div>
  </div>
);

const SsLogo = () => (
  <svg viewBox="0 0 40 40" width="26" height="26" aria-hidden="true">
    <circle cx="20" cy="20" r="9" fill="#D4AF37" />
    <circle cx="20" cy="20" r="5" fill="#FFD700" />
    {[0,45,90,135,180,225,270,315].map((deg, i) => {
      const r = (deg * Math.PI) / 180;
      return <line key={i}
        x1={20+Math.cos(r)*12} y1={20+Math.sin(r)*12}
        x2={20+Math.cos(r)*17} y2={20+Math.sin(r)*17}
        stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" />;
    })}
  </svg>
);

export default ServiceSelect;
